import { HttpErrorResponse } from '@angular/common/http';

/** 403 body reasons the tenant API uses when a create would exceed the plan. */
export const PLAN_LIMIT_PODCASTS_REASON = 'plan_limit_podcasts';
export const PLAN_LIMIT_SEATS_REASON = 'plan_limit_seats';
export type PlanLimitReason = typeof PLAN_LIMIT_PODCASTS_REASON | typeof PLAN_LIMIT_SEATS_REASON;

/** A create the tenant API blocked because the plan of the workspace is used up. */
export interface PlanLimitRejection {
  reason: PlanLimitReason;
  /** What the plan includes; null when the API did not send a usable number. */
  limit: number | null;
  used: number | null;
}

export interface PlanLimitMessage {
  key: string;
  params: Record<string, string | number>;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * Extracts a plan limit rejection from a 403 of the tenant API
 * (`{ code: 403, reason: "plan_limit_podcasts" | "plan_limit_seats", limit, used }`).
 * The body is usually parsed JSON, but text responses carry it as a string.
 */
export function planLimitRejection(error: unknown): PlanLimitRejection | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
    return null;
  }

  let body: unknown = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body) as unknown;
    } catch {
      return null;
    }
  }

  if (body === null || typeof body !== 'object') {
    return null;
  }

  const { reason, limit, used } = body as { reason?: unknown; limit?: unknown; used?: unknown };
  if (reason !== PLAN_LIMIT_PODCASTS_REASON && reason !== PLAN_LIMIT_SEATS_REASON) {
    return null;
  }
  return { reason, limit: count(limit), used: count(used) };
}

/** Translation key and parameters of the toast; singular for a limit of one, a generic text without a limit. */
export function planLimitMessage(rejection: PlanLimitRejection): PlanLimitMessage {
  const prefix = rejection.reason === PLAN_LIMIT_PODCASTS_REASON ? 'PlanLimit.Podcasts' : 'PlanLimit.Seats';
  if (rejection.limit === null) {
    return { key: `${prefix}NoLimit`, params: {} };
  }
  if (rejection.limit === 1) {
    return { key: `${prefix}One`, params: { limit: 1 } };
  }
  return { key: prefix, params: { limit: rejection.limit } };
}
